import React, { useCallback, useEffect, useState } from 'react';
import { FormControlLabel, Switch, TextField } from '@mui/material';
import { MenuBook } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { subjectService } from '../../services/subjectService';
import { Subject } from '../../types';
import SettingsList from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';
import { ToneChip } from '../common/StatusChip';

/** Page size when loading every subject — a settings list is never paged. */
const ALL = 500;

// Subjects are school-wide entities — per-class binding lives in the
// Curriculum module, not on the Subject itself.
const SubjectsSettings: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_subjects');

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Subject | null>(null);
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const all: Subject[] = [];
      for (let page = 1; ; page++) {
        const res = await subjectService.getSubjects({ page, limit: ALL });
        all.push(...res.subjects);
        if (page >= res.pagination.totalPages) break;
      }
      setSubjects(all);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load subjects', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const edit = (s: Subject) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description ?? '', isActive: s.isActive });
  };

  const save = async () => {
    if (!editing || !form.name.trim()) return;
    setSaving(true);
    try {
      await subjectService.updateSubject(editing._id, { ...form, name: form.name.trim() });
      enqueueSnackbar('Subject saved', { variant: 'success' });
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this subject', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsList
        rows={subjects}
        loading={loading}
        rowKey={s => s._id}
        name={s => s.name}
        subtitle={s => s.description || undefined}
        muted={s => !s.isActive}
        meta={s => (s.isActive ? null : <ToneChip label="Inactive" tone="neutral" />)}
        searchText={s => `${s.name} ${s.description ?? ''}`}
        canWrite={writable}
        onEdit={edit}
        remove={{
          run: s => subjectService.deleteSubject(s._id),
          noun: 'subject',
          consequence: () => 'The subject will be removed. A subject with recorded results cannot be deleted — make it inactive instead.',
        }}
        quickAdd={{
          noun: { one: 'subject', many: 'subjects' },
          placeholder: 'Add subjects, e.g. Physics, Chemistry, Biology',
          create: name => subjectService.createSubject({ name }),
          presets: [
            { label: 'Core', names: ['English', 'Urdu', 'Mathematics', 'Islamiat', 'Pakistan Studies'] },
            { label: 'Sciences', names: ['Physics', 'Chemistry', 'Biology', 'Computer Science'] },
            { label: 'Primary', names: ['General Science', 'Social Studies', 'Nazra Quran', 'Art'] },
          ],
        }}
        reload={load}
        empty={{
          icon: <MenuBook />,
          title: 'No subjects yet',
          description: 'Subjects are what the school teaches. Add them above — several at once if you like. Which class studies which subject is set in Curriculum.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title="Edit subject"
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveDisabled={!form.name.trim()}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          error={!form.name.trim()}
          helperText={!form.name.trim() ? 'A subject needs a name' : ' '}
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Optional"
          multiline
          minRows={2}
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
          label="Active — offered when building the curriculum"
        />
      </SettingsDialog>
    </>
  );
};

export default SubjectsSettings;
